import React, { useCallback, useEffect } from "react";
import { IconButton } from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { useAuth0 } from "@auth0/auth0-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../app/store";
import { setToken } from "../../services/auth/authSlice";
import {
  useGetFavoritesQuery,
  useAddFavoriteMutation,
  useRemoveFavoriteMutation,
} from "../../services/favorites/favorites";
import { gaEvent } from "../../analytics";

interface FavoriteButtonProps {
  itemId: string;
  size?: "small" | "medium";
}

const FavoriteButton: React.FC<FavoriteButtonProps> = ({ itemId, size = "small" }) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const dispatch = useDispatch<AppDispatch>();
  const token = useSelector((state: RootState) => state.auth.token);

  useEffect(() => {
    if (isAuthenticated && !token) {
      getAccessTokenSilently().then((t) => dispatch(setToken(t)));
    }
  }, [isAuthenticated, token, getAccessTokenSilently, dispatch]);

  const hasAuth = isAuthenticated && !!token;
  const { data: favorites } = useGetFavoritesQuery(undefined, { skip: !hasAuth });

  const [addFavorite] = useAddFavoriteMutation();
  const [removeFavorite] = useRemoveFavoriteMutation();

  const favorite = favorites?.find((f) => f.item_id === itemId);
  const isFavorite = Boolean(favorite);

  const handleToggle = useCallback(async () => {
    if (!hasAuth) return;

    if (isFavorite && favorite) {
      await removeFavorite(favorite.item_id);
      gaEvent("remove_favorite", { item_id: itemId });
    } else {
      await addFavorite({ item_id: itemId });
      gaEvent("add_favorite", { item_id: itemId });
    }
  }, [hasAuth, isFavorite, favorite, itemId, addFavorite, removeFavorite]);

  if (!isAuthenticated) {
    return (
      <IconButton size={size} disabled>
        <FavoriteBorderIcon fontSize={size} />
      </IconButton>
    );
  }

  return (
    <IconButton size={size} onClick={handleToggle} color={isFavorite ? "error" : "default"}>
      {isFavorite ? <FavoriteIcon fontSize={size} /> : <FavoriteBorderIcon fontSize={size} />}
    </IconButton>
  );
};

export default FavoriteButton;
